import {
  getDirectorDataAndAnalyticsData,
  getDirectorAnalysisData,
  getDDLEthnicityData,
  getDDLAppointmentTypeData,
} from '../config/server-config';
import { API_CALL_SUCCESSFULL, NUMBER_ZERO, NUMBER_TRIPLE_NINE } from '../constants/NumberConstants';
import { GOVERNANCE } from '../constants/ProductConstants';
import { People_search } from './peopleSearch-util';
import { investor_search } from './investor-util';
import api from '../api';

const normalizeGender = (genderLabel) => {
  if (genderLabel === 'All') {
    return null;
  }
  if (genderLabel === 'Male') {
    return 'Male';
  }
  if (genderLabel === 'Female') {
    return 'Female';
  }
  return null;
};

const normalizeRange = (from, to, defaultFrom, defaultTo) => {
  let normalizedFrom = from === null ? defaultFrom : from;
  let normalizedTo = to === null ? defaultTo : to;
  if (from === null && to === null) {
    normalizedFrom = null;
    normalizedTo = null;
  }
  return { from: normalizedFrom, to: normalizedTo };
};

export const getIdsFromSelection = (selection) => {
  if (!selection || selection.length === 0) {
    return null;
  }
  return selection.map((x) => x.value).toString();
};

export const getBoardTypeIdsFromSelection = (selection) => {
  if (!selection || selection.length === 0) {
    return null;
  }
  const filtered = selection.filter((x) => x && x.value !== 0);
  if (filtered.length === 0) {
    return null;
  }
  return filtered.map((x) => x.value).toString();
};

export const getDirectorIdsFromPeopleSelection = (selection) => {
  if (!selection || selection.length === 0) {
    return null;
  }
  const ids = selection.map((x) => x.Director_id ?? x.value).filter((id) => id != null);
  return ids.length > 0 ? ids.toString() : null;
};

export const getBitFilterFromYesNoSelection = (selection, allValue = null) => {
  if (!selection || selection.length === 0) {
    return null;
  }
  if (allValue != null && selection.some((x) => x.value === allValue)) {
    return null;
  }
  const values = selection.map((x) => x.value);
  const hasYes = values.includes(1);
  const hasNo = values.includes(2);
  if (hasYes && hasNo) {
    return null;
  }
  if (hasYes) {
    return 1;
  }
  if (hasNo) {
    return 0;
  }
  return null;
};

export const loadPeopleSearchOptions = async (inputValue) => {
  const result = await investor_search(inputValue, false, 50, 0);
  if (!result?.data) {
    return { data: [] };
  }
  return {
    data: result.data.map((person) => ({
      label: person.investor_name,
      value: person.company_id,
      investor_id: person.investor_id,
      name: person.investor_name,
    })),
  };
};

export const buildDirectorDataAndAnalyticsSearchParams = (props) => {
  let ethnicityIds = null;
  if (props.lstEthnicitySelection?.length > 0) {
    const selection = props.lstEthnicitySelection.map((x) => x.value).toString();
    ethnicityIds = selection === '-1' ? null : selection;
  }

  return {
    gender: props.lstGenderSelection?.label ?? null,
    ethnicity_ids: ethnicityIds,
    tenureFrom: props.tenureFrom ?? null,
    tenureTo: props.tenureTo ?? null,
    boardFrom: props.boardFrom ?? null,
    boardTo: props.boardTo ?? null,
    ageFrom: props.ageFrom ?? null,
    ageTo: props.ageTo ?? null,
    company_search_id: props.companySearchOptionSelection?.value ?? null,
    appointment_type_ids: getIdsFromSelection(props.lstAppointmentTypeSelection),
    board_type_ids: getBoardTypeIdsFromSelection(props.lstBoardTypeSelection),
    is_current: getBitFilterFromYesNoSelection(props.lstCurrentAppointmentSelection),
    is_independent: getBitFilterFromYesNoSelection(props.lstIndependentSelection, 0),
    activist_company_ids: getDirectorIdsFromPeopleSelection(props.lstActivistNomineeSelection),
    director_ids: getDirectorIdsFromPeopleSelection(props.lstSpecificIndividualSelection),
  };
};

export const buildDirectorAnalysisSearchParams = (props, basedOnData) => {
  const baseParams = buildDirectorDataAndAnalyticsSearchParams(props);
  return {
    gender: baseParams.gender,
    tenureFrom: baseParams.tenureFrom,
    tenureTo: baseParams.tenureTo,
    boardFrom: baseParams.boardFrom,
    boardTo: baseParams.boardTo,
    ageFrom: baseParams.ageFrom,
    ageTo: baseParams.ageTo,
    company_search_id: baseParams.company_search_id,
    appointment_type_ids: baseParams.appointment_type_ids,
    based_on_data: basedOnData,
  };
};

const buildNormalizedApiPayload = (arg) => {
  const tenure = normalizeRange(arg.tenureFrom, arg.tenureTo, NUMBER_ZERO, NUMBER_TRIPLE_NINE);
  const board = normalizeRange(arg.boardFrom, arg.boardTo, NUMBER_ZERO, NUMBER_TRIPLE_NINE);
  const age = normalizeRange(arg.ageFrom, arg.ageTo, NUMBER_ZERO, NUMBER_TRIPLE_NINE);

  return {
    gender: normalizeGender(arg.gender),
    tenureFrom: tenure.from,
    tenureTo: tenure.to,
    ageFrom: age.from,
    ageTo: age.to,
    boardFrom: board.from,
    boardTo: board.to,
    company_search_id: arg.company_search_id,
    ethnicity_ids: arg.ethnicity_ids,
    appointment_type_ids: arg.appointment_type_ids,
    board_type_ids: arg.board_type_ids,
    is_current: arg.is_current,
    is_independent: arg.is_independent,
    activist_company_ids: arg.activist_company_ids,
    director_ids: arg.director_ids,
  };
};

export const GetDirectoDataAndAnalyticsData = async (arg) => {
  try {
    const token = localStorage.getItem('token');
    const payload = buildNormalizedApiPayload(arg);
    const response = await api.post(getDirectorDataAndAnalyticsData, {
      ...payload,
      product_id: GOVERNANCE,
      token,
    });
    if (response.status === API_CALL_SUCCESSFULL) {
      return response.data;
    }
  } catch (e) {
    return false;
  }
  return false;
};

export const GetDirectorAnalysisData = async (arg) => {
  try {
    const payload = buildNormalizedApiPayload(arg);
    const response = await api.post(getDirectorAnalysisData, {
      gender: payload.gender,
      tenureFrom: payload.tenureFrom,
      tenureTo: payload.tenureTo,
      ageFrom: payload.ageFrom,
      ageTo: payload.ageTo,
      boardFrom: payload.boardFrom,
      boardTo: payload.boardTo,
      company_search_id: payload.company_search_id,
      based_on_data: arg.based_on_data,
      appointment_type_ids: payload.appointment_type_ids,
    });
    if (response.status === API_CALL_SUCCESSFULL) {
      return response.data;
    }
  } catch (e) {
    return false;
  }
  return false;
};

export const GetDDLEthnicityData = async (arg) => {
  try {
    const response = await api.post(getDDLEthnicityData);
    if (response.status === API_CALL_SUCCESSFULL) {
      return response.data;
    }
  } catch (e) {
    return false;
  }
  return false;
};

export const GetDDLAppointmentTypeData = async (arg) => {
  try {
    const response = await api.post(getDDLAppointmentTypeData);
    if (response.status === API_CALL_SUCCESSFULL) {
      return response.data;
    }
  } catch (e) {
    return false;
  }
  return false;
};
